import {
	Activity,
	BookHeart,
	FileSearch,
	FileText,
	type LucideIcon,
	Stethoscope,
} from "lucide-react";

import type { ClinicaPendencia } from "@/api/db/clinica";
import { SkeletonRows } from "@/components/ui/skeleton";
import { ClinicaLabels } from "@/lib/labels/clinica";
import { cn } from "@/lib/utils";

type PendencyType = ClinicaPendencia["type"];

const TILE_ORDER: PendencyType[] = [
	"transcript_to_link",
	"exam_to_confirm",
	"exam_parked",
	"prontuario_to_review",
	"manual_to_finish",
];

const TILE_ICON: Record<PendencyType, LucideIcon> = {
	transcript_to_link: Stethoscope,
	exam_to_confirm: FileSearch,
	exam_parked: FileText,
	prontuario_to_review: Activity,
	manual_to_finish: BookHeart,
};

const TILE_TARGET: Record<PendencyType, string> = {
	transcript_to_link: "fila-transcricoes",
	exam_to_confirm: "fila-exames",
	exam_parked: "fila-exames-sem-dona",
	prontuario_to_review: "fila-processamento",
	manual_to_finish: "rail-rascunhos",
};

const BLOCKED_TYPES = new Set<PendencyType>(["exam_parked"]);

function countByType(items: ClinicaPendencia[]) {
	const counts = new Map<PendencyType, number>();

	for (const item of items) {
		counts.set(item.type, (counts.get(item.type) ?? 0) + 1);
	}

	return counts;
}

function Tile({ type, count }: { type: PendencyType; count: number }) {
	const Icon = TILE_ICON[type];
	const blocked = BLOCKED_TYPES.has(type);

	return (
		<button
			type="button"
			data-slot={`pendency-tile-${type}`}
			onClick={() => {
				document
					.querySelector(`#${TILE_TARGET[type]}`)
					?.scrollIntoView({ behavior: "smooth", block: "start" });
			}}
			className={cn(
				"surface-panel flex items-center gap-3 p-4 text-left",
				"transition-colors hover:border-primary hover:bg-accent/40",
				"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
			)}
		>
			<span
				className={cn(
					"grid size-10 shrink-0 place-items-center rounded-md",
					blocked ? "bg-warning-soft text-warning" : "bg-primary-soft text-primary",
				)}
			>
				<Icon className="size-4" />
			</span>

			<span className="flex min-w-0 flex-col">
				<span className="metric-value text-foreground">{count}</span>

				<span className="mt-2 text-sm font-medium text-muted-foreground">
					{ClinicaLabels.tile[type](count)}
				</span>
			</span>
		</button>
	);
}

export function PendencySummary({ items }: { items: ClinicaPendencia[] | undefined }) {
	if (items === undefined) {
		return <SkeletonRows rows={1} />;
	}

	if (items.length === 0) {
		return (
			<div data-slot="pendency-all-clear" className="surface-panel flex items-center gap-3 p-3">
				<span className="grid size-10 shrink-0 place-items-center rounded-md bg-success-soft text-success">
					<Stethoscope className="size-4" />
				</span>

				<span className="flex min-w-0 flex-col">
					<span className="text-sm font-semibold text-foreground">
						{ClinicaLabels.allClear.title}
					</span>

					<span className="text-xs text-muted-foreground">
						{ClinicaLabels.allClear.description}
					</span>
				</span>
			</div>
		);
	}

	const counts = countByType(items);

	return (
		<div
			data-component="pendency-summary"
			className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5"
		>
			{TILE_ORDER.filter((type) => (counts.get(type) ?? 0) > 0).map((type) => (
				<Tile key={type} type={type} count={counts.get(type) ?? 0} />
			))}
		</div>
	);
}
